import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FileSystemFileEntry, NgxFileDropEntry} from 'ngx-file-drop';
import {Subject} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {BUTTON_TYPES, BUTTON_STYLES} from "../buttons/buttons.component";

enum FileRejectionReasons {
  Size,
  Type,
  Unknown
}

@Component({
  selector: 'beaconstac-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss']
})
export class UploadComponent implements OnInit{

  BUTTON_TYPES = BUTTON_TYPES;
  BUTTON_STYLES = BUTTON_STYLES;

  @Input()
  width: string = '352px';

  @Input()
  height: string = '167px';

  @Input() inputSubText: string;

  @Input()
  margin: string = '20px';

  contentTypes = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/bmp',
    'image/svg+xml',
    'audio/mp3',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'audio/mpeg',
    'audio/x-m4a',
    'image/webp',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/x-ms-bmp',
    'application/vnd.ms-excel',
    'image/x-icon',
    'image/tiff',
    'audio/vnd.dlna.adts',
    'application/msword',
    'audio/wav',
    'video/mp4',
    'audio/mp4',
    'audio/ogg',
    'text/csv',
    'text/calendar',
    'video/MP2T',
    'video/3gpp',
    'video/quicktime',
    'video/x-m4v',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'font/otf',
    'font/ttf',
  ];
  @Input() file: File = null;
  @Input() acceptTypes: Array<string> = this.contentTypes;
  @Input() maxSize: number = 5e+6;
  @Input() currentProgress: number = 0;
  @Input() isUploading: boolean = false;
  @Input() preview: string;
  @Input() disabled: boolean = false;
  @Input() overrideClick: boolean = false;
  @Input() showRemoveButton: boolean = false;

  @Input() inputText = 'Upload file';
  @Input() hint: string = '';
  @Input() fileURL: string = '';
  @Input() fileName: string = '';
  @Input() dimension: string = '';
  @Input() custom: string = '';
  @Input() small: boolean = false;
  @Input() fileDropSize: string = 'small';
  @Output() fileChange: EventEmitter<File> = new EventEmitter();
  @Output() onClick: EventEmitter<null> = new EventEmitter();
  @Output() onFileRemove: EventEmitter<null> = new EventEmitter();
  @Output() rejectionError: EventEmitter<string> = new EventEmitter();

  @ViewChild('browse', {static: false}) browse: ElementRef;

  // @Input()
  // height: string = '352px'

  isFileOverDropZone: boolean = false;
  _error: string = '';
  _errorTimeout = null;

  hintGoogleDriveUpload: boolean = false;

  fileDroppedSubject = new Subject<null>();
  fileChangedSubject = new Subject<null>();

  clear() {
    this.isFileOverDropZone = false;
    this.error = '';
    this.hintGoogleDriveUpload = false;
    this.file = null;
    this.browse.nativeElement.value = '';
  }

  set error(error: string) {
    this._error = error;
    if (this._errorTimeout) {
      clearTimeout(this._errorTimeout);
      this._errorTimeout = null;
    }
  }


  onClicked() {
    this.onClick.emit();
  }

  onAccept(file: File) {
    if (!file || this.disabled) {
      return;
    }
    this.error = '';
    if (file instanceof File) {
      this.file = file;
    }

    this.isUploading = false;
    this.fileChange.emit(this.file);
  }

  onReject(rejectionReason) {
    this.isUploading = false;
    if (this.disabled) {
      return;
    }
    switch (rejectionReason) {
      case FileRejectionReasons.Type:
        let allowedFileTypes = '.';
        let index = 0;
        for (let fileType of this.acceptTypes) {
          // Special if case for xlsx files, as the file type was not looking good in the frontend.
          if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'){
            fileType = 'xlsx'
          }
          allowedFileTypes += fileType.replace(/.*\//g, '');
          if (index !== this.acceptTypes.length - 1) {
            allowedFileTypes += ' | .';
          }
          index++;
        }
        this.error = `Please upload file with correct extension ( ${allowedFileTypes.trim()} ).`;
        break;
      case FileRejectionReasons.Size:
        this.error = `File too large: exceeds ${this.maxSize / 1000000}MB limit. ${this.hint}`;
        this.hintGoogleDriveUpload = false;
        this.rejectionError.emit('size');
        break;
      default:
        this.error = 'Unable to upload this file';
    }
  }

  onBrowse(file: File) {
    if (this.disabled) {
      return;
    }
    if (!file) {
      this.error = 'Unable to upload this file';
      this.hintGoogleDriveUpload = false;
      this.isUploading = false;
      return;
    }
    if (file.size > this.maxSize) {
      this.onReject(FileRejectionReasons.Size);
      return;
    }
    this.onAccept(file);
  }


  onFileDropped(files: NgxFileDropEntry[]) {
    for (const droppedFile of files) {

      // Check if it is a file?
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if ((fileExtension === 'ttf' || fileExtension === 'otf' ) || ((this.acceptTypes.indexOf('*') !== -1 ||this.acceptTypes.indexOf(file.type) !== -1) && file.size <= this.maxSize)) {
              this.onAccept(file);
            } else {
            if (this.acceptTypes.indexOf(file.type) === -1) {
              this.onReject(FileRejectionReasons.Type)
            } else {
              this.onReject(FileRejectionReasons.Size)
            }
          }
        });
      } else {
        this.onReject(FileRejectionReasons.Unknown)
      }
    }
    this.isFileOverDropZone = false;
  }

  onFileRemoved() {
    this.file = null;
    this.fileName = null;
    this.fileURL = null;
    this.preview = null;
    this.currentProgress = null;
    this.currentProgress = null;
    this.onFileRemove.emit();
  }

  ngOnInit(): void {
    this.fileDroppedSubject.pipe(debounceTime(300)).subscribe(event => {
      this.onFileDropped(event);
    });
    this.fileChangedSubject.pipe(debounceTime(300)).subscribe(event => {
      this.onBrowse(event);
    });
  }

  onFileBrowsed(event) {
    this.isUploading = true;
    this.fileChangedSubject.next(event);
  }
}
